import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useStudyOverview } from '../../../hooks/study/useStudyOverview';

const labelOf = (value) => value || '정보 없음';

const ExamList = () => {
  const { rows, loading } = useStudyOverview();
  const [university, setUniversity] = useState('전체');
  const [year, setYear] = useState('전체');
  const [track, setTrack] = useState('전체');

  const universities = useMemo(
    () => ['전체', ...new Set(rows.map((row) => labelOf(row.university)))],
    [rows]
  );
  const years = useMemo(
    () => ['전체', ...new Set(rows.map((row) => labelOf(row.year)))],
    [rows]
  );
  const tracks = useMemo(
    () => ['전체', ...new Set(rows.map((row) => labelOf(row.track)))],
    [rows]
  );

  const visible = rows.filter((row) => {
    if (university !== '전체' && labelOf(row.university) !== university) return false;
    if (year !== '전체' && labelOf(row.year) !== year) return false;
    if (track !== '전체' && labelOf(row.track) !== track) return false;
    return true;
  });

  return (
    <Page>
      <Kicker>기출</Kicker>
      <Title>풀어 볼 문제를 고르세요</Title>
      <Lead>대학, 연도, 계열이 문제에 들어 있으면 그 값으로 거릅니다. 없는 문제는 정보 없음으로 둡니다.</Lead>

      {loading ? (
        <Empty>문제 목록을 불러오는 중...</Empty>
      ) : (
        <>
          <Filters>
            <Select label="대학" value={university} options={universities} onChange={setUniversity} />
            <Select label="연도" value={year} options={years} onChange={setYear} />
            <Select label="계열" value={track} options={tracks} onChange={setTrack} />
          </Filters>
          {visible.length === 0 ? (
            <Empty>조건에 맞는 문제가 없습니다.</Empty>
          ) : (
            <List>
              {visible.map((row) => (
                <Item key={row.id}>
                  <div>
                    <h2>{row.name}</h2>
                    <p>
                      {labelOf(row.university)} · {labelOf(row.year)} · {labelOf(row.track)}
                    </p>
                  </div>
                  <Open to={`/lecture/${row.id}?step=1`}>풀이 시작</Open>
                </Item>
              ))}
            </List>
          )}
        </>
      )}
    </Page>
  );
};

const Select = ({ label, value, options, onChange }) => (
  <Field>
    <span>{label}</span>
    <select value={value} onChange={(event) => onChange(event.target.value)}>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </Field>
);

export default ExamList;

const Page = styled.div`
  max-width: 1040px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #d9412b;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #1d2340;
`;

const Lead = styled.p`
  margin: 8px 0 20px;
  font-size: 15px;
  line-height: 1.6;
  color: #6f6a5c;
`;

const Filters = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #1d2340;

  select {
    height: 42px;
    padding: 0 10px;
    font: inherit;
    color: #1d2340;
    background: #fff;
    border: 1px solid #e7e1d3;
    border-radius: 6px;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Item = styled.article`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 18px 20px;
  background: #fff;
  border: 1px solid #e7e1d3;
  border-radius: 4px;

  h2 {
    margin: 0;
    font-size: 18px;
    color: #1d2340;
  }

  p {
    margin: 4px 0 0;
    font-size: 13px;
    color: #6f6a5c;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Open = styled(Link)`
  padding: 11px 14px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  text-decoration: none;
  background: #1d2340;
  border-radius: 6px;

  &:hover {
    background: #2c3561;
    text-decoration: none;
  }
`;

const Empty = styled.p`
  margin: 0;
  padding: 22px;
  background: #fff;
  border: 1px dashed #e7e1d3;
  border-radius: 4px;
  color: #6f6a5c;
`;
